/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file chatStoreScope.js
 * @description
 * Which store the Team Chat is scoped to.
 *
 * Team Chat is scoped to ONE store per request. By default that is the account's
 * active store (the app always signs in to `restaurants[0]`), but a colleague who
 * belongs to several stores is invited to channels of ANY of them — so the
 * channels they were added to may sit in a store other than the active one and
 * the chat looks empty. The in-chat store switcher lets them point the chat at
 * another of their stores WITHOUT touching the app's active store.
 *
 * The choice is remembered as `{base, store}` where `base` is the active store at
 * the time of choosing; it only applies while that base is still the active store
 * (a different login / store resets the chat to the new active store).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

/** AsyncStorage key of the remembered chat store. */
export const CHAT_STORE_KEY = 'teamChat.store.v1';

/**
 * Normalise a stored value to `{base, store}` (null when unusable).
 * @param {*} value Parsed storage value.
 * @returns {{base: string, store: string}|null}
 */
export const normalizeStoreChoice = (value) => {
    const base = String(value?.base || '').trim();
    const store = String(value?.store || '').trim();

    return base && store ? {base, store} : null;
};

/**
 * The picked chat store, while it is still valid for the active store.
 * @param {{base: string, store: string}|null} choice Remembered choice.
 * @param {string|null} activeStoreId The account's active store.
 * @returns {string} The picked store id, or '' when the chat follows the active store.
 */
export const pickChatStore = (choice, activeStoreId) => {
    const base = String(activeStoreId || '').trim();

    return choice && base && choice.base === base && choice.store !== base ? choice.store : '';
};

/**
 * Read the remembered choice (null on any storage failure).
 * @returns {Promise<{base: string, store: string}|null>}
 */
export const readChatStoreChoice = async () => {
    try {
        const raw = await AsyncStorage.getItem(CHAT_STORE_KEY);

        return raw ? normalizeStoreChoice(JSON.parse(raw)) : null;
    } catch (_) {
        return null;
    }
};

/**
 * Persist the choice (`null` clears it). Best effort: a failed write only means
 * the pick does not survive a restart.
 * @param {{base: string, store: string}|null} choice
 * @returns {Promise<void>}
 */
export const writeChatStoreChoice = async (choice) => {
    try {
        if (choice) {
            await AsyncStorage.setItem(CHAT_STORE_KEY, JSON.stringify(choice));
        } else {
            await AsyncStorage.removeItem(CHAT_STORE_KEY);
        }
    } catch (_) {
        // storage failure → the pick is in memory only
    }
};

/**
 * Normalise one `GET /api/chat/stores` row (null for an unusable one).
 * @param {Object} row
 * @returns {{restaurantId: string, name: string, enabled: boolean, unreadTotal: number, mentionTotal: number, channelCount: number}|null}
 */
export const normalizeChatStore = (row) => {
    const restaurantId = String(row?.restaurantId || row?._id || '').trim();

    if (!restaurantId) {
        return null;
    }

    return {
        restaurantId,
        name: String(row?.name || '').trim(),
        enabled: row?.enabled !== false,
        unreadTotal: Number(row?.unreadTotal) || 0,
        mentionTotal: Number(row?.mentionTotal) || 0,
        channelCount: Number(row?.channelCount) || 0,
    };
};

/**
 * Split a store listing into the current store and the others.
 * @param {Array<Object>} stores Normalised listing.
 * @param {string|null} currentId Store the chat is scoped to.
 * @returns {{current: Object|null, others: Array<Object>, switchable: Array<Object>, othersWithChats: Array<Object>, othersUnread: number}}
 *   `switchable` = other stores the chat is available in; `othersWithChats` = those where the user is in channels.
 */
export const splitChatStores = (stores, currentId) => {
    const list = Array.isArray(stores) ? stores : [];
    const current = list.find((store) => store.restaurantId === String(currentId || '')) || null;
    const others = list.filter((store) => store !== current);
    const switchable = others.filter((store) => store.enabled);

    return {
        current,
        others,
        switchable,
        othersWithChats: switchable.filter((store) => store.channelCount > 0),
        othersUnread: switchable.reduce((sum, store) => sum + store.unreadTotal, 0),
    };
};

/**
 * The store with the most unread (then most channels) among the stores holding chats.
 * @param {Array<Object>} stores Stores with chats.
 * @returns {Object|null}
 */
export const pickHintStore = (stores) => {
    const list = Array.isArray(stores) ? stores : [];

    return list.length
        ? [...list].sort((a, b) => (b.unreadTotal - a.unreadTotal) || (b.channelCount - a.channelCount))[0]
        : null;
};
